import { BsdsCheckbox } from '@bsds-lib-component/react';
import React, { useEffect, useRef } from 'react';

import type { InvoiceHistoryItem, InvoiceHistoryTableProps } from '../../../types/previdencia/table/invoiceHistoryTableType';
import { generateInvoicesXLSX } from '../../../utils/invoiceExcelUtils';
import { generateInvoicePDF } from '../../../utils/invoicePdfUtils';
import Tooltip from '../../tooltip/Tooltip';
import TableHeaderBulkActionsMenu from '../shared/TableHeaderBulkActionsMenu';
import TableItemActionsMenu from '../shared/TableItemActionsMenu';
import TableMenuItemButton from '../shared/TableMenuItemButton';
import { createMenuItemActionHandlers, registerMapRef } from '../shared/tableMenuUtils';
import { useTableHeaderMenu } from '../shared/useTableHeaderMenu';
import { useTableItemMenu } from '../shared/useTableItemMenu';
import { useTableRowSelection } from '../shared/useTableRowSelection';

const DigitalInvoincingTable: React.FC<InvoiceHistoryTableProps> = ({
  invoices,
  formatCurrency,
  formatDate,
  formatBillingMonth,
  getStatusBadgeClass,
  onRowClick,
  emptyMessage = 'Nenhuma fatura encontrada',
  onSelectionChange,
  onExport,
}) => {
  const emptyStateRef = useRef<HTMLTableCellElement>(null);
  const itemPdfButtonRefs = useRef<Map<string | number, HTMLButtonElement>>(new Map());

  const {
    closeItemMenu,
    isItemMenuOpen,
    handleItemMenuToggle,
    handleItemMenuKeyDown,
    focusMenuButton,
    registerItemMenuRef,
    registerItemMenuButtonRef,
  } = useTableItemMenu<string | number>({
    getFocusTargetOnOpen: (openId) => itemPdfButtonRefs.current.get(openId),
  });

  const {
    isMenuOpen,
    closeMenu,
    toggleMenu,
    menuRef,
    menuButtonRef,
    actionButtonRef,
    menuButtonId,
    handleMenuKeyDown,
    createActionKeyDownHandler,
  } = useTableHeaderMenu();

  const {
    allSelected,
    hasSelectedItems,
    handleSelectAll,
    handleSelectItem,
    isSelected,
    getSelectedItems,
  } = useTableRowSelection({
    itemIds: invoices.map((invoice) => invoice.id),
    onSelectionChange,
  });

  useEffect(() => {
    if (invoices.length === 0) {
      emptyStateRef.current?.focus();
    }
  }, [invoices.length]);

  const handleRowClick = (invoice: InvoiceHistoryItem, event: React.MouseEvent) => {
    if ((event.target as HTMLElement).closest('bsds-checkbox')) {
      return;
    }
    onRowClick?.(invoice);
  };

  const handleRowKeyDown = (invoice: InvoiceHistoryItem, event: React.KeyboardEvent) => {
    if (onRowClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onRowClick(invoice);
    }
  };

  const handleExport = async () => {
    if (!hasSelectedItems) {
      return;
    }

    const selectedInvoices = getSelectedItems(invoices);

    if (onExport) {
      onExport(selectedInvoices);
    } else {
      await generateInvoicesXLSX(selectedInvoices, formatCurrency, formatDate, formatBillingMonth);
    }

    closeMenu();
  };

  const exportPdfActionHandlers = createMenuItemActionHandlers<InvoiceHistoryItem>({
    onAction: (invoice) => {
      void generateInvoicePDF(invoice, formatCurrency, formatDate, formatBillingMonth);
    },
    closeMenu: closeItemMenu,
    focusMenuButton: (invoice) => focusMenuButton(invoice.id),
  });

  return (
    <div className="w-full overflow-x-auto custom-scrollbar rounded-md shadow-xs">
      <div>
        <table
          className="min-w-[800px] w-full table-fixed"
          aria-label="Histórico de faturas"
        >
          <thead>
            <tr>
              <th className="px-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                <Tooltip label="Ações da tabela">
                  <div className="flex items-center gap-1">
                    <TableHeaderBulkActionsMenu
                      isOpen={isMenuOpen}
                      menuButtonId={menuButtonId}
                      menuRef={menuRef}
                      menuButtonRef={menuButtonRef}
                      actionButtonRef={actionButtonRef}
                      actionLabel="Exportar"
                      actionDisabled={!hasSelectedItems}
                      onToggle={(event) => {
                        event.stopPropagation();
                        toggleMenu();
                      }}
                      onMenuKeyDown={handleMenuKeyDown}
                      onAction={(event) => {
                        event.stopPropagation();
                        void handleExport();
                      }}
                      onActionKeyDown={createActionKeyDownHandler(() => void handleExport())}
                      triggerButtonClassName="hover:bg-gray-100 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500 rounded transition-colors"
                    />
                    <Tooltip label="Selecionar todas as faturas">
                      <div className="pl-2">
                        <BsdsCheckbox
                          checked={allSelected}
                          onBsdsChange={handleSelectAll}
                          size="small"
                          color="secondary"
                          aria-label="Selecionar todas as faturas"
                        />
                      </div>
                    </Tooltip>
                  </div>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Mês da Fatura">
                  <span>Mês da Fatura</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Número da Apólice">
                  <span>Número Apólice</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-32" scope="col">
                <Tooltip label="Tipo da fatura">
                  <span>Tipo</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Data de vencimento">
                  <span>Vencimento</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-24" scope="col">
                <Tooltip label="Valor da fatura">
                  <span>Valor</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">
                <Tooltip label="Status da fatura">
                  <span>Status</span>
                </Tooltip>
              </th>
              <th className="px-4 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                <Tooltip label="Ações">
                  <span>Ações</span>
                </Tooltip>
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-50 text-text-color">
            {invoices && invoices.length > 0 ? (
              invoices.map((invoice: InvoiceHistoryItem) => {
                const month = invoice.billingMonth
                  ? (formatBillingMonth ? formatBillingMonth(invoice.billingMonth) : invoice.billingMonth)
                  : (invoice.invoiceMonth || '');
                const policyNumber = invoice.contractNumber || invoice.policyNumber || '';

                return (
                  <tr
                    key={invoice.id}
                    className={`hover:bg-gray-50 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500 transition-colors text-xs ${onRowClick ? 'cursor-pointer' : ''}`}
                    onClick={(e) => handleRowClick(invoice, e)}
                    onKeyDown={(e) => handleRowKeyDown(invoice, e)}
                    aria-selected={isSelected(invoice.id)}
                    aria-label={onRowClick ? `Abrir detalhes da fatura ${month} apólice ${policyNumber}` : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                  >
                    <td className="px-8 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <Tooltip label="Selecionar fatura">
                        <BsdsCheckbox
                          checked={isSelected(invoice.id)}
                          onBsdsChange={() => handleSelectItem(invoice.id)}
                          size="small"
                          color="secondary"
                          aria-label={`Selecionar fatura de ${month}, apólice ${policyNumber}`}
                        />
                      </Tooltip>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{month}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{policyNumber}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{invoice.type}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatDate(invoice.dueDate)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatCurrency(invoice.amount)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`text-xs py-0.5 rounded-xs inline-block min-w-[90px] text-center ${getStatusBadgeClass(invoice.status, 'table')}`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <TableItemActionsMenu
                        itemId={invoice.id}
                        isOpen={isItemMenuOpen(invoice.id)}
                        menuId={`invoice-menu-${invoice.id}`}
                        triggerLabel={`Ações da fatura ${month}`}
                        menuLabel={`Menu de ações da fatura ${month}`}
                        triggerTooltip={`Ações da fatura ${month}`}
                        menuPosition="right"
                        onToggle={(event) => handleItemMenuToggle(invoice.id, event)}
                        onKeyDown={(event) => handleItemMenuKeyDown(invoice.id, event)}
                        registerMenuRef={(element) => registerItemMenuRef(invoice.id, element)}
                        registerButtonRef={(element) => registerItemMenuButtonRef(invoice.id, element)}
                      >
                        <TableMenuItemButton
                          registerRef={(element) => registerMapRef(itemPdfButtonRefs.current, invoice.id, element)}
                          onClick={(event) => exportPdfActionHandlers.handleClick(invoice, event)}
                          onKeyDown={(event) => exportPdfActionHandlers.handleKeyDown(invoice, event)}
                        >
                          Exportar PDF
                        </TableMenuItemButton>
                      </TableItemActionsMenu>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  ref={emptyStateRef}
                  tabIndex={-1}
                  colSpan={8}
                  className="px-4 py-4 text-left sm:text-center text-text-color text-xs focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500"
                  role="status"
                  aria-live="polite"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DigitalInvoincingTable;
